import React, { useState, useEffect } from 'react';
import {
  Box, Button, TextInput, Paper, Stack, Title, Text, Group, Badge,
  ScrollArea, ActionIcon, Loader, Alert, Select, Grid, Container, Divider, Table
} from '@mantine/core';
import {
  IconPlus, IconTrash, IconAlertCircle, IconSend, IconEdit, IconList, IconRefresh, IconEraser, IconBook
} from '@tabler/icons-react';
import Footer from '../../components/Footer';
import PlatformNavBar from '../../components/PlatformNavBar';

// In development the CRA dev server proxies /pavan-api/* → AWS API Gateway (see src/setupProxy.js)
// In production, CORS must be enabled on the API Gateway for direct browser requests to work
const BASE_URL = process.env.NODE_ENV === 'development'
  ? '/pavan-api'
  : 'https://rajftbf2v8.execute-api.us-east-1.amazonaws.com/pavan-api1/ai-agent';
const DEFAULT_USER_ID = '5555555';

const OPERATIONS = [
  { id: 'get_user',            label: 'Get User',            method: 'GET'  },
  { id: 'update_user',         label: 'Update User',         method: 'PUT'  },
  { id: 'add_user',            label: 'Add User',            method: 'POST' },
  { id: 'update_transactions', label: 'Update Transactions', method: 'PUT'  },
  { id: 'api_reference',       label: 'API Reference',       method: null   },
];

const METHOD_COLOR = { GET: 'blue', POST: 'green', PUT: 'orange' };
const STATUS_COLOR  = { Active: 'green', Blocked: 'red', Expired: 'orange' };

const MERCHANTS = [
  'Cave Depot',
  'Knight Vision Gadgets',
  'Capes & Daggers',
  "Alfred's Tailoring",
  'Dark Knight Donuts',
  'Bat Signal Coffee',
  'Two-Face Cosmetics',
  "Penguin's Deli",
  'Arkham Pharmacy',
  "Gordon's Dozen",
  'Batarang Kitchen',
  'WayneCorp Supplies',
  'Gotham Grounds',
  "Robin's Nest",
  'Catwoman Boutique',
  "Riddler's Pizza",
  'Batcave Brewing',
  "Bane's Gym",
  "Ivy's Nursery",
  'Nightwing Noodles',
  'Ace Chemicals',
  "Scarecrow's Market",
  'Batmobile Detailing',
  'Wayne Manor Wines',
  'Mr. Freeze HVAC',
];

function randomTx() {
  const now = new Date();
  const daysAgo = Math.floor(Math.random() * 7) + 1; // 1–7 days ago, never today
  const txDate = new Date(now);
  txDate.setDate(now.getDate() - daysAgo);

  const hh = String(Math.floor(Math.random() * 24)).padStart(2, '0');
  const mm = String(Math.floor(Math.random() * 60)).padStart(2, '0');
  const ss = String(Math.floor(Math.random() * 60)).padStart(2, '0');

  return {
    transaction_id:     `tx-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
    transaction_date:   txDate.toISOString().slice(0, 10),
    transaction_time:   `${hh}:${mm}:${ss}`,
    transaction_amount: (Math.random() * 495 + 5).toFixed(2),
    merchant_name:      MERCHANTS[Math.floor(Math.random() * MERCHANTS.length)],
  };
}

function newTx() {
  return {
    transaction_id: `tx-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
    transaction_date: '',
    transaction_time: '',
    transaction_amount: '',
    merchant_name: '',
  };
}

function emptyUser() {
  return {
    user_id: '',
    name: '',
    DOB: '',
    ZIP_code: '',
    phone_number: '',
    email: '',
    account_balance: '',
    card_status: 'Active',
    replacement_card: 'No',
    priority_shipping: 'No',
    last_transactions: [newTx()],
  };
}

function fmtAmount(val) {
  const n = Number(val);
  return isNaN(n) ? '—' : `$${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

// ─── TransactionEditor ────────────────────────────────────────────────

function TransactionEditor({ transactions, onChange }) {
  function update(idx, field, value) {
    onChange(transactions.map((tx, i) => i === idx ? { ...tx, [field]: value } : tx));
  }
  function add() { onChange([...transactions, newTx()]); }
  function remove(idx) { onChange(transactions.filter((_, i) => i !== idx)); }
  function generateRandom() {
    const count = Math.floor(Math.random() * 3) + 3; // 3–5 transactions
    onChange(Array.from({ length: count }, randomTx));
  }

  return (
    <Stack gap="sm">
      {transactions.map((tx, i) => (
        <Paper key={tx.transaction_id} withBorder p="sm" radius="md" style={{ background: '#fafafa' }}>
          <Group justify="space-between" mb="xs">
            <Text size="xs" fw={700} tt="uppercase" c="dimmed">Transaction {i + 1}</Text>
            {transactions.length > 1 && (
              <ActionIcon size="sm" color="red" variant="subtle" onClick={() => remove(i)}>
                <IconTrash size={14} />
              </ActionIcon>
            )}
          </Group>
          <Grid gutter="xs">
            <Grid.Col span={12}>
              <TextInput size="xs" label="Transaction ID" value={tx.transaction_id}
                onChange={e => update(i, 'transaction_id', e.target.value)} />
            </Grid.Col>
            <Grid.Col span={6}>
              <TextInput size="xs" label="Date (YYYY-MM-DD)" value={tx.transaction_date}
                onChange={e => update(i, 'transaction_date', e.target.value)} placeholder="2026-01-01" />
            </Grid.Col>
            <Grid.Col span={6}>
              <TextInput size="xs" label="Time (HH:MM:SS)" value={tx.transaction_time}
                onChange={e => update(i, 'transaction_time', e.target.value)} placeholder="10:15:30" />
            </Grid.Col>
            <Grid.Col span={6}>
              <TextInput size="xs" label="Amount" value={tx.transaction_amount}
                onChange={e => update(i, 'transaction_amount', e.target.value)} placeholder="0.00" />
            </Grid.Col>
            <Grid.Col span={6}>
              <TextInput size="xs" label="Merchant" value={tx.merchant_name}
                onChange={e => update(i, 'merchant_name', e.target.value)} />
            </Grid.Col>
          </Grid>
        </Paper>
      ))}
      <Group gap="xs">
        <Button size="xs" variant="light" color="purple" leftSection={<IconPlus size={14} />} onClick={add}>
          Add Transaction
        </Button>
        <Button size="xs" variant="light" color="orange" leftSection={<IconRefresh size={14} />} onClick={generateRandom}>
          Generate Random (3–5)
        </Button>
      </Group>
    </Stack>
  );
}

// ─── UserForm ─────────────────────────────────────────────────────────

function UserForm({ value, onChange }) {
  function set(field, val) { onChange({ ...value, [field]: val }); }

  return (
    <Stack gap="md">
      <Grid gutter="sm">
        <Grid.Col span={{ base: 12, sm: 6 }}>
          <TextInput label="User ID" value={value.user_id} onChange={e => set('user_id', e.target.value)} />
        </Grid.Col>
        <Grid.Col span={{ base: 12, sm: 6 }}>
          <TextInput label="Name" value={value.name} onChange={e => set('name', e.target.value)} />
        </Grid.Col>
        <Grid.Col span={{ base: 12, sm: 6 }}>
          <TextInput label="Date of Birth (YYYY-MM-DD)" value={value.DOB}
            onChange={e => set('DOB', e.target.value)} placeholder="1990-01-01" />
        </Grid.Col>
        <Grid.Col span={{ base: 12, sm: 6 }}>
          <TextInput label="ZIP Code" value={value.ZIP_code} onChange={e => set('ZIP_code', e.target.value)} />
        </Grid.Col>
        <Grid.Col span={{ base: 12, sm: 6 }}>
          <TextInput label="Phone Number" value={value.phone_number} onChange={e => set('phone_number', e.target.value)} />
        </Grid.Col>
        <Grid.Col span={{ base: 12, sm: 6 }}>
          <TextInput label="Email" value={value.email} onChange={e => set('email', e.target.value)} />
        </Grid.Col>
        <Grid.Col span={{ base: 12, sm: 6 }}>
          <TextInput label="Account Balance" value={value.account_balance}
            onChange={e => set('account_balance', e.target.value)} placeholder="18500.75" />
        </Grid.Col>
        <Grid.Col span={{ base: 12, sm: 6 }}>
          <Select label="Card Status" value={value.card_status}
            onChange={v => set('card_status', v)} data={['Active', 'Blocked', 'Expired']} />
        </Grid.Col>
        <Grid.Col span={{ base: 12, sm: 6 }}>
          <Select label="Replacement Card" value={value.replacement_card}
            onChange={v => set('replacement_card', v)} data={['Yes', 'No']} />
        </Grid.Col>
        <Grid.Col span={{ base: 12, sm: 6 }}>
          <Select label="Priority Shipping" value={value.priority_shipping}
            onChange={v => set('priority_shipping', v)} data={['Yes', 'No']} />
        </Grid.Col>
      </Grid>
      <Divider label="Transactions" labelPosition="left" />
      <TransactionEditor transactions={value.last_transactions}
        onChange={txs => set('last_transactions', txs)} />
    </Stack>
  );
}

// ─── UserCard ─────────────────────────────────────────────────────────

function UserCard({ user, onLoadForUpdate, onLoadForTransactions, onClearCardFlags }) {
  const txs = Array.isArray(user.last_transactions) ? user.last_transactions : [];

  return (
    <Paper withBorder radius="md" style={{ overflow: 'hidden' }}>

      {/* Header strip */}
      <Group
        p="md"
        justify="space-between"
        wrap="wrap"
        gap="sm"
        style={{ background: '#f3f0ff', borderBottom: '1px solid #e2e6ed' }}
      >
        <Stack gap={4}>
          <Group gap="xs" align="center">
            <Text fw={700} size="lg">{user.name || '—'}</Text>
            <Badge color={STATUS_COLOR[user.card_status] || 'gray'} variant="filled" size="sm">
              {user.card_status || 'Unknown'}
            </Badge>
            {user.replacement_card === 'Yes' && (
              <Badge color="blue" variant="light" size="xs">Replacement Card</Badge>
            )}
            {user.priority_shipping === 'Yes' && (
              <Badge color="teal" variant="light" size="xs">Priority Shipping</Badge>
            )}
          </Group>
          <Text size="xs" c="dimmed" ff="monospace">User ID: {user.user_id}</Text>
        </Stack>

        <Group gap="xs">
          <Button
            size="xs"
            variant="filled"
            color="purple"
            leftSection={<IconEdit size={13} />}
            onClick={onLoadForUpdate}
          >
            Edit User
          </Button>
          <Button
            size="xs"
            variant="light"
            color="teal"
            leftSection={<IconList size={13} />}
            onClick={onLoadForTransactions}
          >
            Edit Transactions
          </Button>
          <Button
            size="xs"
            variant="light"
            color="red"
            leftSection={<IconEraser size={13} />}
            onClick={onClearCardFlags}
          >
            Reset for Demo
          </Button>
        </Group>
      </Group>

      {/* Field grid */}
      <Box p="md">
        <Grid gutter="md" mb={txs.length ? 'lg' : 0}>
          {[
            { label: 'Date of Birth',   value: user.DOB },
            { label: 'ZIP Code',        value: user.ZIP_code },
            { label: 'Phone',           value: user.phone_number },
            { label: 'Email',           value: user.email },
            { label: 'Account Balance', value: fmtAmount(user.account_balance), highlight: true },
          ].map(({ label, value, highlight }) => (
            <Grid.Col key={label} span={{ base: 12, sm: 6, md: 4 }}>
              <Stack gap={2}>
                <Text size="xs" c="dimmed" fw={600} tt="uppercase" style={{ letterSpacing: '0.5px' }}>
                  {label}
                </Text>
                <Text size="sm" fw={highlight ? 700 : 400} c={highlight ? 'violet' : undefined}>
                  {value || '—'}
                </Text>
              </Stack>
            </Grid.Col>
          ))}
        </Grid>

        {/* Transactions table */}
        {txs.length > 0 && (
          <>
            <Divider label={`Transactions (${txs.length})`} labelPosition="left" mb="sm" />
            <Table striped highlightOnHover withTableBorder withColumnBorders>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th style={{ fontSize: '11px', textTransform: 'uppercase', color: '#6b7280' }}>Date</Table.Th>
                  <Table.Th style={{ fontSize: '11px', textTransform: 'uppercase', color: '#6b7280' }}>Time</Table.Th>
                  <Table.Th style={{ fontSize: '11px', textTransform: 'uppercase', color: '#6b7280' }}>Merchant</Table.Th>
                  <Table.Th style={{ fontSize: '11px', textTransform: 'uppercase', color: '#6b7280', textAlign: 'right' }}>Amount</Table.Th>
                  <Table.Th style={{ fontSize: '11px', textTransform: 'uppercase', color: '#6b7280' }}>TX ID</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {txs.map((tx, i) => (
                  <Table.Tr key={tx.transaction_id || i}>
                    <Table.Td style={{ fontSize: '13px' }}>{tx.transaction_date || '—'}</Table.Td>
                    <Table.Td style={{ fontSize: '13px', color: '#6b7280' }}>{tx.transaction_time || '—'}</Table.Td>
                    <Table.Td style={{ fontSize: '13px' }}>{tx.merchant_name || '—'}</Table.Td>
                    <Table.Td style={{ fontSize: '13px', fontWeight: 600, textAlign: 'right', fontFamily: 'monospace' }}>
                      {tx.transaction_amount != null && tx.transaction_amount !== '' ? fmtAmount(tx.transaction_amount) : '—'}
                    </Table.Td>
                    <Table.Td style={{ fontSize: '11px', color: '#9ca3af', fontFamily: 'monospace' }}>
                      {tx.transaction_id || '—'}
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </>
        )}
      </Box>
    </Paper>
  );
}

// ─── ApiReferencePanel ────────────────────────────────────────────────

const BRUCE_WAYNE = {
  user_id: '5555555',
  name: 'Bruce Wayne',
  DOB: '1990-01-01',
  ZIP_code: '12345',
  phone_number: '555-555-5555',
  email: 'bruce.wayne@wayneenterprises.com',
  account_balance: '18500.75',
  card_status: 'Active',
  replacement_card: 'No',
  priority_shipping: 'No',
  last_transactions: [
    { transaction_id: 'tx-1740000001-abc1234', transaction_date: '2026-02-19', transaction_time: '14:32:11', transaction_amount: '47.50',  merchant_name: 'Bat Signal Coffee'    },
    { transaction_id: 'tx-1740000002-def5678', transaction_date: '2026-02-21', transaction_time: '09:15:44', transaction_amount: '215.00', merchant_name: 'Cave Depot'            },
    { transaction_id: 'tx-1740000003-ghi9012', transaction_date: '2026-02-24', transaction_time: '18:45:22', transaction_amount: '89.99',  merchant_name: 'Knight Vision Gadgets' },
  ],
};

const API_ENDPOINTS = [
  {
    method: 'GET',
    path: '/get_user/{user_id}',
    label: 'Get User',
    description: 'Fetch a user record by ID.',
    requestNote: 'GET /get_user/5555555\n\nNo request body.',
    response: BRUCE_WAYNE,
  },
  {
    method: 'PUT',
    path: '/update_user',
    label: 'Update User',
    description: 'Update all fields for an existing user. All fields are sent.',
    request: BRUCE_WAYNE,
    response: { message: 'User updated successfully', user_id: '5555555' },
  },
  {
    method: 'POST',
    path: '/add_user',
    label: 'Add User',
    description: 'Create a new user record.',
    request: BRUCE_WAYNE,
    response: { message: 'User created successfully', user_id: '5555555' },
  },
  {
    method: 'PUT',
    path: '/update_user',
    label: 'Update Transactions',
    description: 'Replace transaction history for a user. Only user_id and last_transactions are required.',
    request: { user_id: '5555555', last_transactions: BRUCE_WAYNE.last_transactions },
    response: { message: 'Transactions updated successfully', user_id: '5555555' },
  },
];

const CODE_STYLE = {
  margin: 0, color: '#e5e7eb', fontSize: '12px',
  fontFamily: 'monospace', whiteSpace: 'pre-wrap', wordBreak: 'break-word',
};

function ApiReferencePanel() {
  return (
    <Stack gap="lg">

      {/* Base URL */}
      <Paper withBorder p="md" radius="md" style={{ background: '#f8f9fb' }}>
        <Stack gap={6}>
          <Text size="xs" fw={700} tt="uppercase" c="dimmed" style={{ letterSpacing: '0.5px' }}>Base URL</Text>
          <Text size="sm" ff="monospace" c="violet" fw={600}>
            https://rajftbf2v8.execute-api.us-east-1.amazonaws.com/pavan-api1/ai-agent
          </Text>
          <Text size="xs" c="dimmed">No authentication required — all requests use Content-Type: application/json.</Text>
        </Stack>
      </Paper>

      {API_ENDPOINTS.map((ep, i) => (
        <Paper key={i} withBorder radius="md" style={{ overflow: 'hidden' }}>

          {/* Endpoint header */}
          <Group p="md" gap="sm" wrap="wrap" style={{ background: '#f3f0ff', borderBottom: '1px solid #e2e6ed' }}>
            <Badge color={METHOD_COLOR[ep.method]} variant="filled" size="md">{ep.method}</Badge>
            <Text ff="monospace" size="sm" fw={700}>{ep.path}</Text>
            <Badge color="gray" variant="light" size="sm">{ep.label}</Badge>
            <Text size="xs" c="dimmed" style={{ marginLeft: 'auto', fontStyle: 'italic' }}>{ep.description}</Text>
          </Group>

          {/* Request / Response columns */}
          <Grid gutter={0}>
            <Grid.Col span={{ base: 12, sm: 6 }} style={{ borderRight: '1px solid #333' }}>
              <Box p="xs" style={{ background: '#252540', borderBottom: '1px solid #333' }}>
                <Text size="xs" c="dimmed" fw={600} tt="uppercase" style={{ letterSpacing: '0.5px' }}>Request Body</Text>
              </Box>
              <ScrollArea h={300} style={{ background: '#1e1e2e' }}>
                <Box p="md">
                  <pre style={CODE_STYLE}>
                    {ep.request ? JSON.stringify(ep.request, null, 2) : ep.requestNote}
                  </pre>
                </Box>
              </ScrollArea>
            </Grid.Col>
            <Grid.Col span={{ base: 12, sm: 6 }}>
              <Box p="xs" style={{ background: '#252540', borderBottom: '1px solid #333' }}>
                <Text size="xs" c="dimmed" fw={600} tt="uppercase" style={{ letterSpacing: '0.5px' }}>Sample Response</Text>
              </Box>
              <ScrollArea h={300} style={{ background: '#1e1e2e' }}>
                <Box p="md">
                  <pre style={CODE_STYLE}>
                    {JSON.stringify(ep.response, null, 2)}
                  </pre>
                </Box>
              </ScrollArea>
            </Grid.Col>
          </Grid>
        </Paper>
      ))}
    </Stack>
  );
}

// ─── ResponsePanel (non-GET operations) ───────────────────────────────

function ResponsePanel({ loading, error, response }) {
  if (loading) {
    return (
      <Paper withBorder p="xl" radius="md" style={{ background: '#1e1e2e' }}>
        <Group justify="center" gap="sm">
          <Loader color="purple" size="sm" />
          <Text c="dimmed" size="sm">Loading...</Text>
        </Group>
      </Paper>
    );
  }
  if (error) {
    return (
      <Alert icon={<IconAlertCircle size={16} />} color="red" radius="md" title="Request Failed">
        <Text size="sm">{error}</Text>
      </Alert>
    );
  }
  if (!response) return null;

  return (
    <Paper withBorder radius="md" style={{ overflow: 'hidden' }}>
      <Group p="sm" style={{ background: '#1e1e2e', borderBottom: '1px solid #333' }}>
        <Badge color={response.ok ? 'green' : 'red'} variant="filled" size="sm">
          {response.status}
        </Badge>
        <Text size="xs" c="dimmed">Response</Text>
      </Group>
      <ScrollArea h={320}>
        <Box p="md" style={{ background: '#1e1e2e' }}>
          <pre style={{
            margin: 0, color: '#e5e7eb', fontSize: '13px',
            fontFamily: 'monospace', whiteSpace: 'pre-wrap', wordBreak: 'break-word'
          }}>
            {JSON.stringify(response.data, null, 2)}
          </pre>
        </Box>
      </ScrollArea>
    </Paper>
  );
}

// ─── Main Component ───────────────────────────────────────────────────

function DemoApiViewerApp({ onBackToPlatformSelector, onBackToHome }) {
  const [activeOp, setActiveOp] = useState('get_user');
  const [getUserId, setGetUserId] = useState(DEFAULT_USER_ID);
  const [updateUserForm, setUpdateUserForm] = useState(emptyUser());
  const [addUserForm, setAddUserForm] = useState(emptyUser());
  const [txUserId, setTxUserId] = useState(DEFAULT_USER_ID);
  const [transactions, setTransactions] = useState([newTx()]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [response, setResponse] = useState(null);
  const [fetchedUser, setFetchedUser] = useState(null);

  useEffect(() => {
    callApi('get_user');
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handle common API Gateway response wrapping patterns
  function extractUser(data) {
    if (!data || typeof data !== 'object') return null;
    if (data.user_id) return data;                       // direct user object
    if (data.user?.user_id) return data.user;            // wrapped in .user key
    if (typeof data.body === 'string') {                 // body is JSON string (API GW proxy style)
      try {
        const parsed = JSON.parse(data.body);
        return parsed?.user_id ? parsed : null;
      } catch { return null; }
    }
    return null;
  }

  async function callApi(opId) {
    setLoading(true);
    setError(null);
    setResponse(null);
    try {
      let url, options;
      if (opId === 'get_user') {
        url = `${BASE_URL}/get_user/${getUserId}`;
        options = { method: 'GET', headers: { 'Content-Type': 'application/json' } };
      } else if (opId === 'update_user') {
        url = `${BASE_URL}/update_user`;
        options = { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(updateUserForm) };
      } else if (opId === 'add_user') {
        url = `${BASE_URL}/add_user`;
        options = { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(addUserForm) };
      } else if (opId === 'update_transactions') {
        url = `${BASE_URL}/update_user`;
        options = { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ user_id: txUserId, last_transactions: transactions }) };
      }
      const res = await fetch(url, options);
      const text = await res.text();
      let data;
      try { data = JSON.parse(text); } catch { data = text; }
      setResponse({ status: res.status, ok: res.ok, data });

      // Cache user data for pre-population on successful fetch
      if (opId === 'get_user' && res.ok) {
        const user = extractUser(data);
        if (user) setFetchedUser(user);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  function handleOpChange(opId) {
    setActiveOp(opId);
    setResponse(null);
    setError(null);
  }

  // Pre-populate the Update User form from the last successful GET and switch tabs
  function loadFetchedUserForUpdate() {
    if (!fetchedUser) return;
    setUpdateUserForm({
      user_id:           String(fetchedUser.user_id ?? ''),
      name:              fetchedUser.name || '',
      DOB:               fetchedUser.DOB || '',
      ZIP_code:          String(fetchedUser.ZIP_code ?? ''),
      phone_number:      String(fetchedUser.phone_number ?? ''),
      email:             fetchedUser.email || '',
      account_balance:   String(fetchedUser.account_balance ?? ''),
      card_status:       fetchedUser.card_status || 'Active',
      replacement_card:  fetchedUser.replacement_card || 'No',
      priority_shipping: fetchedUser.priority_shipping || 'No',
      last_transactions: fetchedUser.last_transactions?.length ? fetchedUser.last_transactions : [newTx()],
    });
    handleOpChange('update_user');
  }

  // Reset card_status → Active, replacement_card → No, priority_shipping → No and save
  async function clearCardFlags() {
    if (!fetchedUser) return;
    setLoading(true);
    setError(null);
    setResponse(null);
    try {
      const payload = { ...fetchedUser, card_status: 'Active', replacement_card: 'No', priority_shipping: 'No' };
      const res = await fetch(`${BASE_URL}/update_user`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const text = await res.text();
      let data;
      try { data = JSON.parse(text); } catch { data = text; }
      setResponse({ status: res.status, ok: res.ok, data });
      if (res.ok) {
        setFetchedUser(prev => ({ ...prev, card_status: 'Active', replacement_card: 'No', priority_shipping: 'No' }));
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  // Pre-populate the Update Transactions form and switch tabs
  function loadFetchedUserForTransactions() {
    if (!fetchedUser) return;
    setTxUserId(String(fetchedUser.user_id ?? ''));
    setTransactions(fetchedUser.last_transactions?.length ? fetchedUser.last_transactions : [newTx()]);
    handleOpChange('update_transactions');
  }

  function getSubmitLabel() {
    if (activeOp === 'get_user')            return 'Fetch User';
    if (activeOp === 'update_user')         return 'Update User';
    if (activeOp === 'add_user')            return 'Add User';
    if (activeOp === 'update_transactions') return 'Update Transactions';
  }

  // Show the structured UserCard for a successful get_user; raw panel for everything else
  const showUserCard = activeOp === 'get_user' && !loading && !error && response?.ok && fetchedUser;

  return (
    <Box style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>

      {/* Top-left navigation: exit to platform selector + return to the SA Tools hub */}
      <Box style={{ background: '#f8f9fb', borderBottom: '1px solid #e2e6ed', padding: '10px 24px' }}>
        <PlatformNavBar
          onBackToPlatformSelector={onBackToPlatformSelector}
          onBackToHome={onBackToHome}
          showHome={!!onBackToHome}
          homeLabel="SA Tools"
          color="purple"
          mb={0}
        />
      </Box>

      {/* Header */}
      <Paper radius={0} style={{
        background: 'linear-gradient(135deg, #764ba2 0%, #667eea 100%)',
        padding: '24px 40px'
      }}>
        <Title order={2} c="white" fw={700}>Presidio Demo API Viewer</Title>
        <Text size="sm" style={{ color: 'rgba(255,255,255,0.8)', marginTop: '4px' }}>
          Banking AI Agent — AWS API Gateway
        </Text>
      </Paper>

      {/* Main content */}
      <Container size="lg" style={{ flex: 1, paddingTop: '32px', paddingBottom: '48px' }}>
        <Stack gap="lg">

          {/* Operation selector */}
          <Group gap="sm" wrap="wrap">
            {OPERATIONS.map(op => {
              const isActive = activeOp === op.id;
              return (
                <Button
                  key={op.id}
                  size="sm"
                  variant={isActive ? 'filled' : 'light'}
                  color={isActive ? (op.id === 'api_reference' ? 'blue' : 'purple') : 'gray'}
                  onClick={() => handleOpChange(op.id)}
                  leftSection={
                    op.method
                      ? <Badge size="xs" color={METHOD_COLOR[op.method]} variant="filled" style={{ pointerEvents: 'none' }}>{op.method}</Badge>
                      : <IconBook size={14} />
                  }
                >
                  {op.label}
                </Button>
              );
            })}
          </Group>

          {/* API Reference panel */}
          {activeOp === 'api_reference' && <ApiReferencePanel />}

          {/* Form area */}
          {activeOp !== 'api_reference' && <Paper withBorder p="xl" radius="md">
            <Stack gap="md">

              {/* Get User */}
              {activeOp === 'get_user' && (
                <Stack gap="sm">
                  <Text size="sm" fw={600} c="purple">Fetch user record by ID</Text>
                  <Text size="sm" c="dimmed">
                    Use your work number <strong>minus the area code</strong> as your account number.
                    For example, Bruce Wayne's work number is <strong>972-555-5555</strong>, making his account number <strong>5555555</strong>.
                  </Text>
                  <Group align="flex-end" gap="sm">
                    <TextInput
                      label="User ID"
                      value={getUserId}
                      onChange={e => setGetUserId(e.target.value)}
                      style={{ flex: 1, maxWidth: 300 }}
                      placeholder="5555555"
                    />
                  </Group>
                </Stack>
              )}

              {/* Update User */}
              {activeOp === 'update_user' && (
                <Stack gap="sm">
                  <Text size="sm" fw={600} c="purple">Update existing user — all fields are sent</Text>
                  <UserForm value={updateUserForm} onChange={setUpdateUserForm} />
                </Stack>
              )}

              {/* Add User */}
              {activeOp === 'add_user' && (
                <Stack gap="sm">
                  <Text size="sm" fw={600} c="purple">Create a new user record</Text>
                  <UserForm value={addUserForm} onChange={setAddUserForm} />
                </Stack>
              )}

              {/* Update Transactions */}
              {activeOp === 'update_transactions' && (
                <Stack gap="sm">
                  <Text size="sm" fw={600} c="purple">Replace transaction history for a user</Text>
                  <TextInput
                    label="User ID"
                    value={txUserId}
                    onChange={e => setTxUserId(e.target.value)}
                    style={{ maxWidth: 300 }}
                    placeholder="5555555"
                  />
                  <Divider label="Transactions" labelPosition="left" />
                  <TransactionEditor transactions={transactions} onChange={setTransactions} />
                </Stack>
              )}

              <Divider />
              <Group>
                <Button
                  leftSection={<IconSend size={16} />}
                  color="purple"
                  loading={loading}
                  onClick={() => callApi(activeOp)}
                >
                  {getSubmitLabel()}
                </Button>
              </Group>
            </Stack>
          </Paper>}

          {/* Result area — UserCard for GET success, raw panel for everything else */}
          {activeOp !== 'api_reference' && (
            showUserCard
              ? (
                <UserCard
                  user={fetchedUser}
                  onLoadForUpdate={loadFetchedUserForUpdate}
                  onLoadForTransactions={loadFetchedUserForTransactions}
                  onClearCardFlags={clearCardFlags}
                />
              )
              : <ResponsePanel loading={loading} error={error} response={response} />
          )}

        </Stack>
      </Container>

      <Footer platformColor="purple" />
    </Box>
  );
}

export default DemoApiViewerApp;
